import type { Category, EventListItem, MarketListItem, Platform } from '@/types/market'
import type { BackendEvent, BackendMarket } from '../backend-types'

// Map backend category to our category type
function mapCategory(category: string): Category {
	const lower = category.toLowerCase()
	const categoryMap: Record<string, Category> = {
		politics: 'politics',
		crypto: 'crypto',
		economy: 'economy',
		sports: 'sports',
		entertainment: 'entertainment',
		science: 'science',
	}
	return categoryMap[lower] || 'other'
}

// Map backend source to platform
function mapPlatform(source: string): Platform {
	return source === 'kalshi' ? 'kalshi' : 'polymarket'
}

// Parse string number to number
function parseNumber(value: string | undefined | null): number {
	if (!value) return 0
	const parsed = Number.parseFloat(value)
	return Number.isNaN(parsed) ? 0 : parsed
}

// Parse ISO date to unix timestamp (seconds)
function parseDate(isoDate: string | undefined | null): number | undefined {
	if (!isoDate) return undefined
	const ts = Date.parse(isoDate)
	return Number.isNaN(ts) ? undefined : Math.floor(ts / 1000)
}

export function normalizeBackendEvent(event: BackendEvent): EventListItem {
	const isKalshi = event.source === 'kalshi'

	const markets = (event.topMarkets || []).map(m => ({
		// Kalshi IDs must be uppercase
		id: isKalshi ? m.id.toUpperCase() : m.id,
		title: m.shortTitle || m.question,
		yesPrice: parseNumber(m.bestAsk),
		status: 'active' as const,
	}))

	return {
		// Kalshi IDs must be uppercase
		id: isKalshi ? event.slug.toUpperCase() : event.slug,
		platform: mapPlatform(event.source),
		title: event.title,
		subtitle: event.description,
		imageUrl: event.image,
		category: mapCategory(event.category),
		endDate: parseDate(event.endDate),
		volume: parseNumber(event.volume),
		volume24h: parseNumber(event.volume24h),
		liquidity: parseNumber(event.liquidity),
		marketCount: event.topMarkets?.length || 0,
		markets: markets.length > 0 ? markets : undefined,
		isInactive: event.closed || !event.active,
	}
}

export function normalizeBackendEvents(events: BackendEvent[]): EventListItem[] {
	return events.map(normalizeBackendEvent)
}

// Market normalization
export function normalizeBackendMarket(market: BackendMarket): MarketListItem {
	// Determine if market is tradable onchain
	// Polymarket: always tradable
	// Kalshi: tradable if yesMint is set (solana accounts initialized)
	const isTradable = market.source === 'polymarket' ? true : !!market.yesMint

	return {
		id: market.sourceId,
		platform: mapPlatform(market.source),
		title: market.question,
		subtitle: market.shortTitle,
		imageUrl: market.image,
		category: mapCategory(market.category || 'other'),
		yesPrice: market.outcomePrices?.[0] ?? 0,
		noPrice: market.outcomePrices?.[1] ?? 0,
		volume: parseNumber(market.volume),
		volume24h: parseNumber(market.volume24h),
		liquidity: parseNumber(market.liquidity),
		openInterest: 0, // Not provided in backend response
		closeTime: (parseDate(market.endDate) ?? 0) * 1000, // convert to ms
		status: market.closed ? 'closed' : 'active',
		isTradable,
		isNew: false, // Not provided in backend response
		isArchived: false, // Not provided in backend response
		eventId: market.eventId,
		eventTitle: market.question, // Use question as event title fallback
		parentEvent: market.slug
			? { parentEventSlug: market.slug, parentEventId: market.eventId }
			: undefined,
	}
}

export function normalizeBackendMarkets(markets: BackendMarket[]): MarketListItem[] {
	return markets.map(normalizeBackendMarket)
}
